import { EventType } from "@/analytics/analytics-events";
import { StartEditWaypointsToolProperties } from "@/analytics/edit-waypoints-tool-properties";
import { useCanChangeTool } from "@/hooks/use-can-change-tool";
import { selectModeName } from "@/store/mode-selectors";
import { changeMode } from "@/store/mode-slice";
import { selectActiveArea, selectAreaFor } from "@/store/selections-selectors";
import { setActiveArea, setActiveElement } from "@/store/selections-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { selectActiveTool } from "@/store/ui/ui-selectors";
import {
  activateTool,
  setNextModeActiveTool,
  ToolName,
} from "@/store/ui/ui-slice";
import { EditWaypointsIcon, FaroMenuItem } from "@faro-lotv/flat-ui";
import { Analytics } from "@faro-lotv/foreign-observers";
import { GUID } from "@faro-lotv/foundation";
import { selectIElement } from "@faro-lotv/project-source";
import { useCallback, type ComponentProps, type JSX } from "react";
import { ContextMenuActionType, type ContextMenuAction } from "../action-types";

function EnableEditWaypointsAction({
  elementId,
  closeContextMenu,
  dark,
}: ComponentProps<
  NonNullable<ContextMenuAction["CustomComponent"]>
>): JSX.Element | null {
  const startEditWaypointsTool = useEnableEditWaypointsCallback(
    elementId,
    closeContextMenu,
  );

  const canChangeTool = useCanChangeTool();

  return (
    <FaroMenuItem
      dark={dark}
      label="Move Waypoint"
      secondaryText="Enables edit waypoints mode"
      onClick={startEditWaypointsTool}
      Icon={EditWaypointsIcon}
      disabled={!canChangeTool}
    />
  );
}

/**
 * An action of the project tree context menu, that enables the edit waypoints tool
 */
export const ENABLE_EDIT_WAYPOINTS_ACTION: ContextMenuAction = {
  type: ContextMenuActionType.enableEditWaypoints,
  CustomComponent: EnableEditWaypointsAction,
};

/**
 *
 * @param elementId ID of the selected waypoint
 * @param closeContextMenu Callback invoked to close the context menu
 * @returns A callback that stars the edit waypoints tool, in sheet mode, for the area containing the given elementId
 */
function useEnableEditWaypointsCallback(
  elementId: GUID,
  closeContextMenu: () => void,
): () => void {
  const activeTool = useAppSelector(selectActiveTool);
  const currentMode = useAppSelector(selectModeName);
  const dispatch = useAppDispatch();

  const element = useAppSelector(selectIElement(elementId));
  const currentArea = useAppSelector(selectAreaFor(element));
  const activeArea = useAppSelector(selectActiveArea);

  return useCallback(() => {
    Analytics.track<StartEditWaypointsToolProperties>(
      EventType.startEditWaypointsTool,
      {
        via: "capture tree context menu",
      },
    );

    // Ensuring the right area is selected
    if (currentArea && currentArea.id !== activeArea?.id) {
      dispatch(setActiveArea(currentArea.id));
      dispatch(setActiveElement(currentArea.id));
    }

    if (currentMode !== "sheet") {
      // If the Viewer is not in sheet mode, save in the store the tool that
      // should be activated when entering sheet mode.
      dispatch(setNextModeActiveTool(ToolName.editWaypoints));
      dispatch(changeMode("sheet"));
    } else if (activeTool !== ToolName.editWaypoints) {
      dispatch(activateTool(ToolName.editWaypoints));
    }

    closeContextMenu();
  }, [
    closeContextMenu,
    activeTool,
    dispatch,
    currentMode,
    currentArea,
    activeArea,
  ]);
}
